import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ClientBasedStatisticsData, ClientStatisticsData, DijiKolayChartItem, PaginatedClientStatisticsResponse, SubscriptionManagementData } from './dashboard.types';

export interface DashboardState {
    dijiKolayChart: {
        data: DijiKolayChartItem[] | null;
        loading: boolean;
        error: string | null;
    };
    clientStatistics: {
        data: ClientStatisticsData | null;
        loading: boolean;
        error: string | null;
    };
    subscriptionManagement: {
        data: SubscriptionManagementData | null;
        loading: boolean;
        error: string | null;
    };
    clientBaseStatistics: {
        data: ClientBasedStatisticsData[] | [];
        loading: boolean;
        error: string | null;
        total: number;
        page: number;
        limit: number;
        cache: Record<string, { data: ClientBasedStatisticsData[], total: number }>;
    };
    allCompanyNames: {
        data: string[];
        loading: boolean;
        error: string | null;
    };
}

const initialState: DashboardState = {
    dijiKolayChart: {
        data: null,
        loading: false,
        error: null
    },
    clientStatistics: {
        data: null,
        loading: false,
        error: null,
    },
    subscriptionManagement: {
        data: null,
        loading: false,
        error: null
    },
    clientBaseStatistics: {
        data: [],
        loading: false,
        error: null,
        total: 0,
        page: 1,
        limit: 10,
        cache: {}
    },
    allCompanyNames: {
        data: [],
        loading: false,
        error: null
    }
};

const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState,
    reducers: {
        fetchDijiKolayChartRequest(state) {
            state.dijiKolayChart.loading = true;
            state.dijiKolayChart.error = null;
        },
        fetchDijiKolayChartSuccess(state, action: PayloadAction<DijiKolayChartItem[]>) {
            state.dijiKolayChart.loading = false;
            state.dijiKolayChart.data = action.payload;
        },
        fetchDijiKolayChartFailure(state, action: PayloadAction<string>) {
            state.dijiKolayChart.loading = false;
            state.dijiKolayChart.error = action.payload;
        },

        fetchClientStatisticsRequest(state) {
            state.clientStatistics.loading = true;
            state.clientStatistics.error = null;
        },
        fetchClientStatisticsSuccess(state, action: PayloadAction<ClientStatisticsData>) {
            state.clientStatistics.loading = false;
            state.clientStatistics.data = action.payload;
        },
        fetchClientStatisticsFailure(state, action: PayloadAction<string>) {
            state.clientStatistics.loading = false;
            state.clientStatistics.error = action.payload;
        },

        fetchSubscriptionManagementRequest(state) {
            state.subscriptionManagement.loading = true;
            state.subscriptionManagement.error = null;
        },
        fetchSubscriptionManagementSuccess(state, action: PayloadAction<SubscriptionManagementData>) {
            state.subscriptionManagement.loading = false;
            state.subscriptionManagement.data = action.payload;
        },
        fetchSubscriptionManagementFailure(state, action: PayloadAction<string>) {
            state.subscriptionManagement.loading = false;
            state.subscriptionManagement.error = action.payload;
        },

        fetchClientBasedStatisticsRequest(state, action: PayloadAction<{ page: number; limit: number; companyName?: string[] }>) {
            state.clientBaseStatistics.loading = true;
            state.clientBaseStatistics.error = null;
            state.clientBaseStatistics.page = action.payload.page;
            state.clientBaseStatistics.limit = action.payload.limit;
        },
        fetchClientBasedStatisticsSuccess(state, action: PayloadAction<{ data: ClientBasedStatisticsData[]; total: number; page: number; limit: number; cacheKey: string }>) {
            state.clientBaseStatistics.loading = false;
            state.clientBaseStatistics.data = action.payload.data;
            state.clientBaseStatistics.total = action.payload.total;
            state.clientBaseStatistics.page = action.payload.page;
            state.clientBaseStatistics.limit = action.payload.limit;
            state.clientBaseStatistics.cache[action.payload.cacheKey] = {
                data: action.payload.data,
                total: action.payload.total
            };
        },
        fetchClientBasedStatisticsFailure(state, action: PayloadAction<string>) {
            state.clientBaseStatistics.loading = false;
            state.clientBaseStatistics.error = action.payload;
        },

        fetchAllCompanyNamesRequest(state) {
            state.allCompanyNames.loading = true;
            state.allCompanyNames.error = null;
        },
        fetchAllCompanyNamesSuccess(state, action: PayloadAction<string[]>) {
            state.allCompanyNames.loading = false;
            state.allCompanyNames.data = action.payload;
        },
        fetchAllCompanyNamesFailure(state, action: PayloadAction<string>) {
            state.allCompanyNames.loading = false;
            state.allCompanyNames.error = action.payload;
        },
    },
});

export const {
    fetchDijiKolayChartRequest,
    fetchDijiKolayChartSuccess,
    fetchDijiKolayChartFailure,

    fetchClientStatisticsRequest,
    fetchClientStatisticsSuccess,
    fetchClientStatisticsFailure,

    fetchSubscriptionManagementRequest,
    fetchSubscriptionManagementSuccess,
    fetchSubscriptionManagementFailure,

    fetchClientBasedStatisticsRequest,
    fetchClientBasedStatisticsSuccess,
    fetchClientBasedStatisticsFailure,

    fetchAllCompanyNamesRequest,
    fetchAllCompanyNamesSuccess,
    fetchAllCompanyNamesFailure,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;